import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

// Shared physical resources — e.g. pottery wheels, easels, kiln slots.
// Multiple events can share a pool; bookings across all of them compete for the same capacity.
export default function ResourcePoolsAdmin() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [draftCap, setDraftCap] = useState(1);

  const load = () => {
    setLoading(true);
    api.get("/admin/resource-pools")
      .then(r => setPools(r.data.pools || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (p) => {
    try {
      await api.put(`/admin/resource-pools/${p.name}`, { label: p.label, capacity: Number(p.capacity) });
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Save failed")); }
  };

  const create = async () => {
    if (!draftName.trim() || !draftLabel.trim()) {
      toast.error("Please fill both fields");
      return;
    }
    try {
      await api.put(`/admin/resource-pools/${draftName.trim().toLowerCase().replace(/ /g, "-")}`, {
        label: draftLabel.trim(),
        capacity: Math.max(0, Number(draftCap) || 0),
      });
      setDraftName(""); setDraftLabel(""); setDraftCap(1);
      toast.success("Pool created");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Create failed")); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete pool "${p.label}"? Events referencing it will lose their capacity cap.`)) return;
    try {
      await api.delete(`/admin/resource-pools/${p.name}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-resource-pools">
      <Toaster position="top-center" richColors />

      <div className="mb-8">
        <div className="overline">Capacity</div>
        <h1 className="display text-4xl mt-2">Shared resources</h1>
        <p className="text-muted2 text-sm mt-2 max-w-2xl">
          Group multiple events that share the same physical resource (e.g. all wheel-throwing classes share 5 pottery wheels). On any given
          date and time slot, total bookings across every event in the pool are capped at the capacity below. Walk-ins count too.
        </p>
      </div>

      {/* Existing pools */}
      <div className="space-y-3 mb-10">
        {pools.map((p, i) => (
          <div key={p.name} className="border border-soft p-4 flex items-center gap-4" data-testid={`pool-row-${p.name}`}>
            <div className="w-40 shrink-0">
              <div className="overline">Name</div>
              <div className="font-mono text-sm mt-1">{p.name}</div>
            </div>
            <div className="flex-1">
              <div className="overline">Label</div>
              <input
                value={p.label}
                onChange={e => setPools(list => list.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                className="w-full border-b border-soft py-1.5 bg-transparent text-sm"
                data-testid={`pool-label-${p.name}`}
              />
            </div>
            <div className="w-28 shrink-0">
              <div className="overline">Capacity</div>
              <input
                type="number" min="0"
                value={p.capacity}
                onChange={e => setPools(list => list.map((x, j) => j === i ? { ...x, capacity: e.target.value } : x))}
                className="w-full border-b border-soft py-1.5 bg-transparent text-sm"
                data-testid={`pool-capacity-${p.name}`}
              />
            </div>
            <button onClick={() => save(p)} className="btn-primary text-xs px-3 py-1.5" data-testid={`pool-save-${p.name}`}><Save size={12}/> Save</button>
            <button onClick={() => remove(p)} className="p-2 text-muted2 hover:text-terra" data-testid={`pool-delete-${p.name}`}><Trash2 size={14}/></button>
          </div>
        ))}
        {!loading && pools.length === 0 && <div className="text-muted2 text-sm">No resource pools yet.</div>}
      </div>

      {/* Create new */}
      <div className="border border-soft p-6 bg-cream-2 max-w-2xl">
        <div className="overline mb-3">Add a new pool</div>
        <div className="grid md:grid-cols-3 gap-4">
          <label className="text-xs">
            <div className="text-muted2 mb-1">Name (slug, no spaces)</div>
            <input value={draftName} onChange={e => setDraftName(e.target.value)} placeholder="e.g. pottery-wheel" className="w-full border-b border-soft py-2 bg-transparent" data-testid="pool-new-name"/>
          </label>
          <label className="text-xs">
            <div className="text-muted2 mb-1">Label</div>
            <input value={draftLabel} onChange={e => setDraftLabel(e.target.value)} placeholder="Pottery wheels" className="w-full border-b border-soft py-2 bg-transparent" data-testid="pool-new-label"/>
          </label>
          <label className="text-xs">
            <div className="text-muted2 mb-1">Capacity</div>
            <input type="number" min="0" value={draftCap} onChange={e => setDraftCap(e.target.value)} className="w-full border-b border-soft py-2 bg-transparent" data-testid="pool-new-cap"/>
          </label>
        </div>
        <button onClick={create} className="btn-primary mt-6 text-xs" data-testid="pool-new-save"><Plus size={12}/> Create pool</button>
      </div>
    </div>
  );
}
